import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Optimizes photo URLs for faster loading by handling different storage types
 * and adding size parameters when possible
 * 
 * @param photoUrl - The photo URL from the database
 * @param size - The desired size (small, medium, large)
 * @param creatorId - Optional creator ID to use proxy for Tally URLs
 */
export function getOptimizedPhotoUrl(
  photoUrl?: string | null, 
  size: 'small' | 'medium' | 'large' = 'small',
  creatorId?: string
): string | undefined {
  if (!photoUrl) return undefined;
  
  // URLs API locales (creator photos cache) - return as-is
  if (photoUrl.startsWith('/api/')) {
    return photoUrl;
  }
  
  // Chemin Object Storage direct (legacy) - return as-is
  if (photoUrl.startsWith('/objects/')) {
    return photoUrl;
  }
  
  // URLs Tally (storage.tally.so) - return direct URL (has accessToken)
  if (photoUrl.startsWith('http') && photoUrl.includes('storage.tally.so')) {
    return photoUrl;
  }

  // Other external URLs - return as-is
  if (photoUrl.startsWith('http')) {
    return photoUrl;
  }

  // Photos uploadées via le formulaire de candidature (/uploads/creators/uuid.jpg)
  if (photoUrl.startsWith('/uploads/')) {
    return photoUrl;
  }

  // Ancien fichier local - vérifier si le chemin commence déjà par /creators/
  if (photoUrl.startsWith('/creators/')) {
    return photoUrl;
  }

  // Sinon, préfixer avec /creators/
  return `/creators/${photoUrl}`;
}

/**
 * Extracts error message from API response error object
 * Type-safe alternative to casting error as any
 * 
 * @param error - Unknown error object (typically from API call)
 * @returns Error message string
 */
export function getApiErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  
  // Check for structured API error response
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error
  ) {
    const response = error.response;
    if (
      typeof response === 'object' &&
      response !== null &&
      'data' in response
    ) {
      const data = response.data;
      if (
        typeof data === 'object' &&
        data !== null &&
        'error' in data
      ) {
        const errorMsg = (data as { error?: string }).error;
        if (typeof errorMsg === 'string') {
          return errorMsg;
        }
      }
    }
  }
  
  return String(error);
}
